<template>
  <div
    ref="shell"
    class="portal-home-layout portal-home-layout--app-shell d-flex flex-grow-1 min-h-0 h-100"
    :style="shellStyle"
  >
    <v-navigation-drawer
      v-model="drawerOpen"
      temporary
      location="start"
      width="330"
      class="portal-home-layout__drawer border-e"
    >
      <portal-sidebar-nav
        class="flex-grow-1 overflow-y-auto pa-2 pt-4"
        :items="visibleMenuTree"
        @nav="onSidebarNav"
      />

      <template #append>
        <div class="pa-3 border-t portal-home-layout__drawer-footer">
          <v-btn
            block
            variant="text"
            :prepend-icon="logoutIcon"
            class="text-none"
            @click="logout"
          >
            Cerrar sesión
          </v-btn>
        </div>
      </template>
    </v-navigation-drawer>

    <div
      class="portal-home-layout__main-col d-flex flex-column flex-grow-1 min-w-0 min-h-0"
    >
      <header
        class="portal-home-layout__toolbar d-flex align-center flex-shrink-0 bg-surface pl-2"
      >
        <v-btn
          icon
          variant="text"
          aria-label="Abrir menú"
          density="comfortable"
          class="ms-1"
          @click="drawerOpen = true"
        >
          <v-icon :icon="menuToolbarIcon" size="24" />
        </v-btn>

        <div
          class="portal-home-layout__header-brand d-flex align-center gap-3 min-w-0 flex-grow-1 ml-4"
        >
          <div
            v-if="logoUrl"
            class="portal-home-layout__header-logo flex-shrink-0"
            role="button"
            tabindex="0"
            aria-label="Ir al inicio"
            @click="goHome"
            @keydown.enter.prevent="goHome"
            @keydown.space.prevent="goHome"
          >
            <img
              v-if="logoUseNativeImg"
              :src="logoUrl"
              :alt="hospitalName"
              class="portal-home-layout__header-logo-img"
            />

            <v-img
              v-else
              :src="logoUrl"
              :alt="hospitalName"
              max-height="40"
              max-width="120"
              contain
            />
          </div>

          <div
            v-else
            class="text-h6 font-weight-bold text-truncate portal-home-layout__header-logo-fallback"
            :style="{ color: 'var(--portal-primary)' }"
            role="button"
            tabindex="0"
            aria-label="Ir al inicio"
            @click="goHome"
            @keydown.enter.prevent="goHome"
            @keydown.space.prevent="goHome"
          >
            {{ hospitalInitial }}
          </div>
        </div>

        <v-menu
          v-model="accountMenuOpen"
          location="bottom end"
          offset="12"
          :close-on-content-click="false"
          content-class="portal-home-layout__account-menu"
        >
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="text"
              class="portal-home-layout__user-trigger mr-2"
            >
              <span class="portal-home-layout__user-label-wrap min-w-0">
                <span
                  class="text-body-2 text-truncate portal-home-layout__user-label text-secondary"
                >
                  {{ headerUserLabel }}
                </span>
              </span>
              <v-icon :icon="userIcon" color="secondary" size="30" />
            </v-btn>
          </template>

          <div class="portal-home-layout__account-card bg-surface">
            <div class="d-flex align-center portal-home-layout__account-summary">
              <v-avatar
                size="40"
                color="secondary"
                variant="tonal"
                class="portal-home-layout__account-avatar flex-shrink-0"
              >
                <v-icon :icon="userIcon" size="30" />
              </v-avatar>

              <div class="min-w-0">
                <div class="portal-home-layout__account-name text-secondary">
                  {{ accountDisplayName }}
                </div>
              </div>
            </div>

            <v-divider class="portal-home-layout__account-divider" />

            <div class="portal-home-layout__theme-toggle">
              <v-icon :icon="themeIcon" size="22" color="secondary" />
              <span class="text-body-2 font-weight-medium">
                Modo oscuro
              </span>
              <v-spacer />
              <v-switch
                v-model="darkMode"
                color="secondary"
                density="compact"
                hide-details
                inset
                aria-label="Modo oscuro"
                @update:model-value="onDarkModePreferenceChange"
              />
            </div>

            <v-divider class="portal-home-layout__account-divider" />

            <v-list
              density="comfortable"
              class="portal-home-layout__account-actions py-0"
            >
              <v-list-item
                v-for="item in accountMenuItems"
                :key="item.path"
                rounded="lg"
                :prepend-icon="item.icon"
                :title="item.title"
                @click="onAccountMenuNav(item.path)"
              />
              <v-divider
                v-if="accountMenuItems.length"
                class="portal-home-layout__logout-divider"
              />
              <v-list-item
                rounded="lg"
                :prepend-icon="logoutIcon"
                title="Cerrar sesión"
                @click="onAccountMenuLogout"
              />
            </v-list>
          </div>
        </v-menu>
      </header>

      <v-main class="portal-home-layout__main flex-grow-1 pa-0 bg-surface">
        <template v-if="$slots.default">
          <v-fade-transition mode="out-in">
            <slot />
          </v-fade-transition>
        </template>
        <router-view v-else v-slot="{ Component }">
          <v-fade-transition mode="out-in">
            <component :is="Component" :key="portalViewKey" />
          </v-fade-transition>
        </router-view>
      </v-main>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

import enums from "../../../utils/enums";

import PortalSidebarNav from "../../../components/layout/PortalSidebarNav.vue";

import {
  resolveHospitalLogoUrl,
  hospitalLogoUsesNativeImg,
} from "../../../utils/helpers/hospitalLogoUrl";

import { normalizePortalModulePath } from "../../../utils/helpers/normalizePortalModulePath";

import { collectTileItems } from "../../../utils/helpers/homeMenuTiles";
import { derivePortalThemeColors } from "../../../utils/helpers/portalColorTheme";
import {
  clearDarkModePreference,
  resolveInitialDarkMode,
  saveDarkModePreference,
} from "../../../utils/helpers/portalDarkModePreference";

// Temporal hasta version 4.3: Consulta de Internaciones depende de cambios de Historia Clinica.
const TEMPORARILY_DISABLED_PORTAL_MODULES = Object.freeze([]);

export default {
  name: "PortalHomeLayout",

  components: { PortalSidebarNav },

  provide() {
    return {
      portalHomeLayout: this,
    };
  },

  data() {
    return {
      drawerOpen: false,
      windowWidth:
        typeof window !== "undefined" ? window.innerWidth : 960,
      menuToolbarIcon: enums.icons.MENU,
      userIcon: enums.icons.ACCOUNT_OUTLINE,
      logoutIcon: enums.icons.LOGOUT,
      themeIcon: enums.icons.THEME_LIGHT_DARK,
      accountMenuOpen: false,
      darkMode: resolveInitialDarkMode(),
      vinculosFamiliares: [],
      vinculosFamiliaresCargados: false,
      titularNombre: "",
      titularApellido: "",
    };
  },

  computed: {
    ...mapState("portalHome", [
      "menuTree",
      "menuLoading",
      "menuError",
      "profileNombre",
      "profileApellido",
      "activePortalFichaId",
      "portalPrivateParams",
    ]),
    ...mapState("portalConfig", ["hospitalConfig"]),
    hospitalName() {
      return this.hospitalConfig.name || "Portal";
    },
    hospitalInitial() {
      return (this.hospitalName || "?").charAt(0).toUpperCase();
    },
    logoUrl() {
      return resolveHospitalLogoUrl(this.hospitalConfig.logoUrl);
    },
    logoUseNativeImg() {
      return hospitalLogoUsesNativeImg(this.logoUrl);
    },
    shellStyle() {
      const colors = this.portalThemeColors;
      return {
        "--portal-primary": colors.primary,
        "--portal-secondary": colors.secondary,
        "--portal-surface": colors.surface,
        "--portal-background": colors.background,
      };
    },
    portalThemeColors() {
      return derivePortalThemeColors(
        this.hospitalConfig.primaryColor || "#00838F",
        this.hospitalConfig.secondaryColor || "#E0F2F1",
        this.darkMode,
      );
    },
    menuTileItems() {
      return collectTileItems(this.visibleMenuTree);
    },
    visibleMenuTree() {
      return this.filterVisibleMenuTree(this.menuTree);
    },
    menuTitle() {
      const path = this.$route.path ?? "";
      if (path === "/" || path === "") return "Inicio";
      const routeKey = normalizePortalModulePath(path).toLowerCase();
      const item = this.findMenuItemByRouteKey(this.visibleMenuTree, routeKey);
      const t = item?.title;
      if (t != null && String(t).trim() !== "") return String(t).trim();
      const doc = this.$route.meta?.documentTitle;
      if (doc != null && String(doc).trim() !== "") {
        return String(doc).trim();
      }
      const parts = path.split("/").filter(Boolean);
      const last = parts[parts.length - 1];
      return last ? last.replace(/-/g, " ") : "Portal";
    },
    /** En viewport < sm (600px) solo nombre; si no hay, apellido; en desktop nombre y apellido. */
    headerUserLabel() {
      const n = String(this.titularNombre || this.profileNombre || "").trim();
      const a = String(this.titularApellido || this.profileApellido || "").trim();
      const isMobileToolbar = this.windowWidth < 600;
      if (isMobileToolbar) {
        const solo = n || a;
        return solo ? solo.toUpperCase() : "USUARIO LOGUEADO";
      }
      const line = `${n} ${a}`.trim().toUpperCase();
      return line || "USUARIO LOGUEADO";
    },
    accountDisplayName() {
      return this.titularDisplayName || "USUARIO LOGUEADO";
    },
    visualizandoFamiliar() {
      return Number(this.activePortalFichaId) > 0;
    },
    titularDisplayName() {
      const n = String(this.titularNombre || this.profileNombre || "").trim();
      const a = String(this.titularApellido || this.profileApellido || "").trim();
      const line = `${n} ${a}`.trim();
      return line || "Mi portal";
    },
    familiaresConfirmados() {
      return (this.vinculosFamiliares || []).filter((v) => v?.confirmado === true);
    },
    portalSwitchItems() {
      const titular = {
        key: "titular",
        fichaId: null,
        title: this.titularDisplayName,
        subtitle: "Mi portal",
        icon: enums.icons.ACCOUNT,
        active: !this.visualizandoFamiliar,
      };
      const familiares = this.familiaresConfirmados.map((v) => {
        const id = Number(v?.fichaFamiliarId);
        const nombre = this.formatNombreApellidoPaciente(v);
        const parentesco = String(v?.parentescoNombre ?? "").trim();
        return {
          key: `familiar-${id}`,
          fichaId: Number.isFinite(id) && id > 0 ? id : null,
          title: nombre || "Familiar",
          subtitle: parentesco || "Familiar confirmado",
          parentesco,
          icon: enums.icons.ACCOUNT_MULTIPLE,
          active: Number(this.activePortalFichaId) === id,
        };
      }).filter((item) => item.fichaId);
      return [titular, ...familiares];
    },
    activePortalSwitchKey() {
      const active = this.portalSwitchItems.find((item) => item.active);
      return active?.key || "titular";
    },
    activePortalSwitchIcon() {
      const active = this.portalSwitchItems.find((item) => item.key === this.activePortalSwitchKey);
      return active?.icon || enums.icons.ACCOUNT;
    },
    portalViewKey() {
      return `${this.$route.fullPath}::${this.activePortalFichaId || 'titular'}`;
    },
    accountMenuItems() {
      if (this.visualizandoFamiliar) return [];
      return [
        {
          title: this.menuTitleForPath("/patientdata", "Mis datos"),
          path: "/patientdata",
          icon: enums.icons.ACCOUNT,
        },
        {
          title: "Cambiar contraseña",
          path: "/changepassword",
          icon: enums.icons.CHANGE_PASSWORD,
        },
      ].filter((item) => this.hasMenuPath(item.path));
    },
  },
  watch: {
    hospitalConfig: {
      handler() {
        this.applyShellVars();
      },
      deep: true,
    },
    "$route.path"() {
      this.drawerOpen = false;
      this.accountMenuOpen = false;
      this.redirectIfPersonalRouteDisabled();
    },
    activePortalFichaId() {
      this.redirectIfPersonalRouteDisabled();
    },
    menuTitle: {
      immediate: true,
      handler(t) {
        document.title = `${t} · Portal pacientes`;
      },
    },
    darkMode: {
      immediate: true,
      handler(value) {
        this.applyDarkMode(value);
      },
    },
  },
  async mounted() {
    this.windowWidth = window.innerWidth;
    window.addEventListener("resize", this.onToolbarResize, { passive: true });
    this.startSystemDarkModeListener();
    this.applyShellVars();

    await Promise.all([
      this.fetchHomeMenu(),
      this.fetchHomeProfile({ fichaId: null }),
      this.cargarVinculosFamiliares(),
    ]);
    this.titularNombre = String(this.profileNombre ?? "").trim();
    this.titularApellido = String(this.profileApellido ?? "").trim();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.onToolbarResize);
    this.stopSystemDarkModeListener();
  },
  methods: {
    ...mapActions({
      fetchHomeMenu: 'portalHome/fetchHomeMenu',
      fetchHomeProfile: 'portalHome/fetchHomeProfile',
      fetchPortalPrivateParams: 'portalHome/fetchPortalPrivateParams',
      fetchInformesLaboratorioUrlExterna: 'portalHome/fetchInformesLaboratorioUrlExterna',
      fetchVinculos: 'grupoFamiliar/fetchVinculos',
    }),
    ...mapMutations({
      setActivePortalFicha: 'portalHome/setActivePortalFicha',
      resetPortalHomeSession: 'portalHome/resetPortalHomeSession',
    }),
    onToolbarResize() {
      this.windowWidth = window.innerWidth;
    },
    applyShellVars() {
      const el = this.$refs.shell;
      if (el?.style) {
        const colors = this.portalThemeColors;
        el.style.setProperty("--portal-primary", colors.primary);
        el.style.setProperty("--portal-secondary", colors.secondary);
        el.style.setProperty("--portal-surface", colors.surface);
        el.style.setProperty("--portal-background", colors.background);
      }
    },
    applyDarkMode(value) {
      const themeName = value ? "dark" : "light";
      if (typeof this.$vuetify?.theme?.change === "function") {
        this.$vuetify.theme.change(themeName);
      } else {
        const themeRef = this.$vuetify?.theme?.global?.name;
        if (themeRef && typeof themeRef === "object" && "value" in themeRef) {
          themeRef.value = themeName;
        }
      }
      document.documentElement.classList.toggle("portal-dark-mode", !!value);
      this.applyShellVars();
    },
    onDarkModePreferenceChange(value) {
      saveDarkModePreference(value);
    },
    startSystemDarkModeListener() {
      if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
      this._portalColorSchemeMql = window.matchMedia("(prefers-color-scheme: dark)");
      this._portalColorSchemeHandler = (event) => {
        clearDarkModePreference();
        this.darkMode = event.matches;
      };
      if (typeof this._portalColorSchemeMql.addEventListener === "function") {
        this._portalColorSchemeMql.addEventListener("change", this._portalColorSchemeHandler);
      } else if (typeof this._portalColorSchemeMql.addListener === "function") {
        this._portalColorSchemeMql.addListener(this._portalColorSchemeHandler);
      }
    },
    stopSystemDarkModeListener() {
      if (!this._portalColorSchemeMql || !this._portalColorSchemeHandler) return;
      if (typeof this._portalColorSchemeMql.removeEventListener === "function") {
        this._portalColorSchemeMql.removeEventListener("change", this._portalColorSchemeHandler);
      } else if (typeof this._portalColorSchemeMql.removeListener === "function") {
        this._portalColorSchemeMql.removeListener(this._portalColorSchemeHandler);
      }
    },
    async cargarVinculosFamiliares() {
      this.vinculosFamiliaresCargados = false;
      try {
        const rows = await this.fetchVinculos({ fichaId: null });
        this.vinculosFamiliares = Array.isArray(rows) ? rows : [];
      } catch {
        this.vinculosFamiliares = [];
      } finally {
        this.vinculosFamiliaresCargados = true;
      }
    },
    isPersonalPortalPath(rawPath) {
      const key = normalizePortalModulePath(rawPath).replace(/^\/+/, "").toLowerCase();
      return ["personal", "patientdata", "familygroup", "changepassword"].includes(key);
    },
    isPersonalPortalMenuItem(item) {
      if (this.isPersonalPortalPath(item?.path)) return true;
      const title = String(item?.title ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();
      return title === "personal";
    },
    isTemporarilyDisabledPortalModule(item) {
      const keys = [
        item?.path,
        item?.name,
        item?.route,
      ].map((value) =>
        normalizePortalModulePath(value)
          .replace(/^\/+/, "")
          .toLowerCase(),
      );
      return keys.some((key) =>
        TEMPORARILY_DISABLED_PORTAL_MODULES.includes(key),
      );
    },
    filterVisibleMenuTree(nodes) {
      if (!Array.isArray(nodes)) return [];
      return nodes
        .map((n) => {
          const children = this.filterVisibleMenuTree(n.children ?? []);
          if (this.isTemporarilyDisabledPortalModule(n)) return null;
          if (this.visualizandoFamiliar && this.isPersonalPortalMenuItem(n)) return null;
          if (Array.isArray(n.children) && n.children.length) {
            const path = n.path ?? "";
            if (!children.length && !path) return null;
            return { ...n, children };
          }
          return { ...n };
        })
        .filter(Boolean);
    },
    findMenuItemByRouteKey(nodes, routeKey) {
      if (!Array.isArray(nodes)) return null;
      for (const n of nodes) {
        const raw = n.path ?? "";
        const k = normalizePortalModulePath(raw).toLowerCase();
        if (k === routeKey) return n;
        const children = n.children ?? [];
        const found = this.findMenuItemByRouteKey(children, routeKey);
        if (found) return found;
      }
      return null;
    },
    hasMenuPath(rawPath) {
      if (this.visualizandoFamiliar && this.isPersonalPortalPath(rawPath)) return false;
      const routeKey = normalizePortalModulePath(rawPath).toLowerCase();
      return !!this.findMenuItemByRouteKey(this.visibleMenuTree, routeKey);
    },
    menuTitleForPath(rawPath, fallback) {
      const routeKey = normalizePortalModulePath(rawPath).toLowerCase();
      const item = this.findMenuItemByRouteKey(this.visibleMenuTree, routeKey);
      const title = item?.title;
      return title != null && String(title).trim() !== ""
        ? String(title).trim()
        : fallback;
    },
    async navigateToModulePath(rawPath) {
      const target = normalizePortalModulePath(rawPath);
      if (this.isTemporarilyDisabledPortalPath(target)) {
        if (this.$route.path !== "/") this.$router.push({ path: "/" });
        return;
      }
      if (this.visualizandoFamiliar && this.isPersonalPortalPath(target)) {
        if (this.$route.path !== "/") this.$router.push({ path: "/" });
        return;
      }
      if (target === "/" || target === "") {
        if (this.$route.path !== "/") this.$router.push({ path: "/" });
        return;
      }
      const moduleKey = target.replace(/^\/+/, "").toLowerCase();
      if (moduleKey === "labreports" || moduleKey === "externallabreports") {
        if (this.portalPrivateParams?.urlExternaResultadosLaboratorio == null) {
          await this.fetchPortalPrivateParams();
        }
        const hasUrlExternaConfigurada = String(
          this.portalPrivateParams?.urlExternaResultadosLaboratorio ?? "",
        ).trim().length > 0;
        if (!hasUrlExternaConfigurada) {
          this.$router.push({ path: target });
          return;
        }
        try {
          const url = await this.fetchInformesLaboratorioUrlExterna();
          if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
          }
        } catch {
          // Si falla la URL externa, cae en navegación local al módulo.
        }
      }
      this.$router.push({ path: target });
    },
    isTemporarilyDisabledPortalPath(rawPath) {
      const key = normalizePortalModulePath(rawPath)
        .replace(/^\/+/, "")
        .split("/")[0]
        .toLowerCase();
      return TEMPORARILY_DISABLED_PORTAL_MODULES.includes(key);
    },
    goHome() {
      if (this.$route.path !== "/") {
        this.$router.push({ path: "/" }).catch(() => {});
      }
    },
    async onSidebarNav(item) {
      const path = item.path;
      await this.navigateToModulePath(path);
      this.drawerOpen = false;
    },
    async onAccountMenuNav(path) {
      await this.navigateToModulePath(path);
      this.accountMenuOpen = false;
    },
    async seleccionarPortal(item) {
      this.setActivePortalFicha({
        fichaId: item.fichaId,
        nombre: item.fichaId ? item.title : "",
        parentesco: item.fichaId ? item.parentesco : "",
      });
      this.accountMenuOpen = false;
      this.redirectIfPersonalRouteDisabled();
      if (this.$route.name === "portal-mis-turnos") {
        this.$store.dispatch("portalHome/fetchTurnosPendientes");
        if (this.$store.state.portalHome?.turnosHistorial?.fetched) {
          this.$store.dispatch("portalHome/fetchTurnosHistorialPaciente");
        }
      }
    },
    redirectIfPersonalRouteDisabled() {
      if (!this.visualizandoFamiliar) return;
      if (!this.isPersonalPortalPath(this.$route.path)) return;
      if (this.$route.path !== "/") {
        this.$router.replace({ path: "/" }).catch(() => {});
      }
    },
    seleccionarPortalPorKey(key) {
      const item = this.portalSwitchItems.find((x) => x.key === key);
      if (item) this.seleccionarPortal(item);
    },
    onAccountMenuLogout() {
      this.accountMenuOpen = false;
      this.logout();
    },
    logout() {
      localStorage.removeItem("token");
      this.resetPortalHomeSession();
      this.$router.push({ name: "login" });
    },
    formatNombreApellidoPaciente(paciente) {
      const nombre = String(paciente?.nombre ?? "").trim();
      const apellido = String(paciente?.apellido ?? "").trim();
      const nombreApellido = `${nombre} ${apellido}`.trim();
      return nombreApellido || String(paciente?.nombreCompleto ?? "").trim();
    }
  },
};
</script>

<style scoped>
.portal-home-layout {
  --portal-primary: #00838f;
  --portal-secondary: #e0f2f1;
  --portal-surface: #ffffff;
  --portal-background: #f7f9fb;
}
.portal-home-layout--app-shell {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
.portal-home-layout__drawer {
  background-color: rgb(var(--v-theme-surface)) !important;
}
.portal-home-layout__drawer-footer {
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.portal-dark-mode .portal-home-layout__drawer-footer {
  border-color: rgba(255, 255, 255, 0.1) !important;
}
.portal-home-layout__drawer :deep(.v-list-item:not(.v-list-item--active)) {
  color: rgba(0, 0, 0, 0.87);
}
.portal-dark-mode
  .portal-home-layout__drawer
  :deep(.v-list-item:not(.v-list-item--active)) {
  color: rgba(var(--v-theme-on-surface), 0.9);
}
.portal-home-layout__drawer
  :deep(.v-list-item:not(.v-list-item--active) .v-icon) {
  color: rgba(0, 0, 0, 0.54);
}
.portal-dark-mode
  .portal-home-layout__drawer
  :deep(.v-list-item:not(.v-list-item--active) .v-icon) {
  color: rgba(var(--v-theme-on-surface), 0.7);
}
.portal-home-layout__drawer :deep(.v-list-item--active) {
  background-color: rgb(var(--v-theme-primary)) !important;

  color: #fff !important;
}
.portal-home-layout__drawer :deep(.v-list-item--active .v-icon) {
  color: #fff !important;
}
.portal-dark-mode .portal-home-layout__drawer :deep(.v-list-item--active),
.portal-app--dark .portal-home-layout__drawer :deep(.v-list-item--active) {
  color: rgb(var(--v-theme-on-primary)) !important;
}
.portal-dark-mode
  .portal-home-layout__drawer
  :deep(.v-list-item--active .v-icon),
.portal-app--dark
  .portal-home-layout__drawer
  :deep(.v-list-item--active .v-icon) {
  color: rgb(var(--v-theme-on-primary)) !important;
}
.portal-home-layout__drawer :deep(.v-list-item--active .v-list-item__overlay) {
  opacity: 0 !important;
}
.portal-home-layout__drawer :deep(.v-list-item:hover > .v-list-item__overlay) {
  opacity: 0.06;
}
.portal-home-layout__drawer
  :deep(.v-list-item--active:hover > .v-list-item__overlay) {
  opacity: 0 !important;
}
.portal-home-layout__drawer :deep(.v-list-group__items .v-list-item) {
  padding-inline-start: 28px !important;
}
.portal-home-layout__drawer :deep(.v-list-group__header .v-list-item) {
  color: rgba(0, 0, 0, 0.87);
}
.portal-dark-mode
  .portal-home-layout__drawer
  :deep(.v-list-group__header .v-list-item) {
  color: rgba(var(--v-theme-on-surface), 0.9);
}
.portal-home-layout__toolbar {
  flex-wrap: nowrap;
  min-height: 56px;
  z-index: 2;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06) !important;
  border-bottom: 2px solid #eceff1 !important;
}
.portal-dark-mode .portal-home-layout__toolbar {
  border-bottom-color: rgba(255, 255, 255, 0.1) !important;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35) !important;
}
.portal-home-layout__header-logo-img {
  display: block;
  max-height: 40px;
  max-width: 120px;
  width: auto;
  height: auto;
  object-fit: contain;
}
.portal-home-layout__header-logo,
.portal-home-layout__header-logo-fallback {
  cursor: pointer;
}
.portal-home-layout__header-logo:focus-visible,
.portal-home-layout__header-logo-fallback:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 4px;
  border-radius: 4px;
}
.portal-home-layout__user-trigger {
  min-width: 0;
  max-width: min(50vw, 340px);
  height: 48px !important;
  padding-inline: 8px 10px !important;
  text-transform: none;
  letter-spacing: 0;
}
.portal-home-layout__user-trigger :deep(.v-btn__content) {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}
.portal-home-layout__user-label-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  line-height: 1.12;
}
.portal-home-layout__user-label {
  display: block;
  min-width: 0;
  max-width: 34vw;
  font-weight: bold;
}
.portal-home-layout__account-card {
  width: min(300px, calc(100vw - 16px));
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.18);
  padding: 10px;
  position: relative;
  margin-top: 5px;
}
.portal-dark-mode .portal-home-layout__account-card {
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
}
.portal-home-layout__account-card::before {
  content: "";
  position: absolute;
  top: -11px;
  right: 38px;
  width: 22px;
  height: 22px;
  background: rgb(var(--v-theme-surface));
  border-left: 1px solid rgba(0, 0, 0, 0.12);
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  transform: rotate(45deg);
}
.portal-dark-mode .portal-home-layout__account-card::before {
  border-left-color: rgba(255, 255, 255, 0.1);
  border-top-color: rgba(255, 255, 255, 0.1);
}
.portal-home-layout__account-summary {
  gap: 16px;
}
.portal-home-layout__account-avatar {
  border: 2px solid rgba(var(--v-theme-secondary), 0.35);
  margin-left: 8px;
}
.portal-home-layout__account-name {
  max-width: 250px;
  font-size: 1rem;
  line-height: 1.15;
  font-weight: 700;
  overflow-wrap: anywhere;
}
.portal-home-layout__account-divider {
  margin: 8px 0 8px;
}
.portal-home-layout__theme-toggle {
  min-height: 44px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px 0 8px;
}
.portal-home-layout__theme-toggle :deep(.v-switch) {
  flex: 0 0 auto;
}
.portal-home-layout__logout-divider {
  margin: 6px 0;
}
.portal-home-layout__account-actions {
  background: transparent;
}
.portal-home-layout__account-actions :deep(.v-list-item) {
  min-height: 44px;
  padding-inline: 4px 8px;
  font-size: 1rem;
}
.portal-home-layout__account-actions :deep(.v-list-item-title) {
  font-size: 0.98rem;
  white-space: normal;
}
@media (min-width: 600px) {
  .portal-home-layout__user-label {
    max-width: 280px;
  }
}
@media (max-width: 599px) {
  .portal-home-layout__user-trigger {
    max-width: calc(100vw - 88px);
  }
  .portal-home-layout__account-card {
    padding: 18px 20px 14px;
  }
  .portal-home-layout__account-card::before {
    right: 28px;
  }
  .portal-home-layout__account-summary {
    gap: 12px;
  }
  .portal-home-layout__account-avatar {
    width: 62px !important;
    height: 62px !important;
  }
  .portal-home-layout__account-avatar :deep(.v-icon) {
    font-size: 46px !important;
  }
  .portal-home-layout__account-name {
    max-width: 230px;
    font-size: 1.25rem;
  }
}
.portal-home-layout__main {
  background: rgb(var(--v-theme-background));
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}
.portal-home-layout__main :deep(.v-main__wrap) {
  min-height: 0;
}
.min-w-0 {
  min-width: 0;
}
</style>
